import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@service-victoria/component-library/components/Button/Button';
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { useForm } from 'react-hook-form';

import InputFieldWithCheckBox from 'Components/InputFieldWithCheckBox';
import { Field } from 'Components/InputFieldWithCheckBox/InputFieldWithCheckBox';
import { schema } from 'schema/checkinSchema';
import { CHECKIN_FIELDS } from 'utils/constants';

import * as Styled from './styled';

export interface PersonFormValue {
  firstName: string;
  lastName: string;
  contactNumber: string;
  isLastNameSetToDefaultValue?: boolean;
  isContactNumberSetToDefaultValue?: boolean;
}

interface AccordionCheckinFormProps {
  forNewGuest?: boolean;
  firstName?: Field;
  lastName?: Field;
  contactNumber?: Field;
  primaryAction: {
    text: string;
    onClick: (person: PersonFormValue) => void;
  };
  secondaryAction?: {
    text: string;
    onClick: () => void;
  };
  onUpdateFormRef?: (formHandleRef: AccordionCheckinFormHandle | null) => void;
}

export interface AccordionCheckinFormHandle {
  getFormValues: () => PersonFormValue;
  resetToDefaultValue: () => void;
  isFormValid: () => Promise<boolean>;
}

const AccordionCheckinForm = React.forwardRef<
  AccordionCheckinFormHandle,
  AccordionCheckinFormProps
>(
  (
    {
      firstName,
      contactNumber,
      lastName,
      primaryAction,
      secondaryAction,
      onUpdateFormRef,
      forNewGuest
    },
    ref
  ) => {
    const {
      register,
      getValues,
      handleSubmit,
      formState: { errors, isDirty, touchedFields },
      setValue,
      reset,
      watch,
      trigger,
      setFocus
    } = useForm<PersonFormValue>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        firstName: firstName?.value ?? firstName?.defaultValue ?? '',
        lastName: lastName?.value ?? lastName?.defaultValue ?? '',
        contactNumber: contactNumber?.value ?? contactNumber?.defaultValue ?? '',
        isLastNameSetToDefaultValue: lastName?.checkbox?.defaultChecked,
        isContactNumberSetToDefaultValue: contactNumber?.checkbox?.defaultChecked
      },
      resolver: yupResolver(schema)
    });

    const handle = useRef<AccordionCheckinFormHandle>({
      getFormValues: () => {
        return getValues();
      },
      resetToDefaultValue: () => {
        reset();
      },
      isFormValid: () => {
        // purposely using trigger rather than the
        // isValid from formState to ensure that
        // the ref can get the latest is valid state.
        return trigger();
      }
    });
    useImperativeHandle(ref, () => handle.current);

    useEffect(() => {
      if (isDirty) {
        onUpdateFormRef?.(handle.current);
      }
    }, [isDirty, onUpdateFormRef]);

    useEffect(() => {
      if (forNewGuest) {
        // force form to be dirty
        setValue('firstName', getValues('firstName'), { shouldDirty: true });
        onUpdateFormRef?.(handle.current);
      }
    }, [forNewGuest, getValues, onUpdateFormRef, setValue]);

    const isLastNameSetToDefaultValue = watch('isLastNameSetToDefaultValue');
    const isContactNumberSetToDefaultValue = watch('isContactNumberSetToDefaultValue');

    useEffect(() => {
      if (isLastNameSetToDefaultValue && lastName?.defaultValue) {
        setValue('lastName', lastName.defaultValue);
      }
    }, [isLastNameSetToDefaultValue, lastName?.defaultValue, setValue]);
    useEffect(() => {
      if (isContactNumberSetToDefaultValue && contactNumber?.defaultValue) {
        setValue('contactNumber', contactNumber.defaultValue);
      }
    }, [isContactNumberSetToDefaultValue, contactNumber?.defaultValue, setValue]);

    useEffect(() => {
      setFocus('firstName');

      return () => {
        onUpdateFormRef?.(null);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Styled.AccordionItemContainer action='#' onSubmit={handleSubmit(primaryAction.onClick)}>
        <InputFieldWithCheckBox
          field={firstName}
          {...register('firstName')}
          {...CHECKIN_FIELDS.FIRST_NAME}
          error={touchedFields.firstName && errors.firstName?.message}
        />
        <InputFieldWithCheckBox
          field={lastName}
          checkboxProps={register('isLastNameSetToDefaultValue')}
          {...register('lastName')}
          {...CHECKIN_FIELDS.LAST_NAME}
          error={touchedFields.lastName && errors.lastName?.message}
          checked={isLastNameSetToDefaultValue}
        />
        <InputFieldWithCheckBox
          field={contactNumber}
          checkboxProps={register('isContactNumberSetToDefaultValue')}
          {...register('contactNumber')}
          {...CHECKIN_FIELDS.CONTACT_NUMBER}
          type='tel'
          error={touchedFields.contactNumber && errors.contactNumber?.message}
          checked={isContactNumberSetToDefaultValue}
        />
        <Styled.CallToActionContainer>
          <Button type='submit' data-testid='AccordionFormSubmitButton'>
            {primaryAction.text}
          </Button>
          {secondaryAction && (
            <Button
              buttonType='secondary'
              onClick={secondaryAction.onClick}
              data-testid='AccordionFormSecondaryButton'>
              {secondaryAction.text}
            </Button>
          )}
        </Styled.CallToActionContainer>
      </Styled.AccordionItemContainer>
    );
  }
);

export default AccordionCheckinForm;
